import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  getDispatchingRules,
  getDispatchingRuleDetail,
  createDispatchingRule,
  updateDispatchingRule,
  deleteDispatchingRule,
  toggleDispatchingRule
} from '@/service/orderTest'

// ==================== 模拟数据 ====================

// 模拟派单规则数据
const mockRules = [
  {
    id: 1,
    ruleName: '产康服务自动派单规则',
    serviceType: 'maternity',
    radius: 5,
    matchConditions: {
      includeUpcoming: true,
      minRating: 4.0,
      maxDistance: 5
    },
    pushSettings: {
      pushCount: 3,
      timeout: 30
    },
    enabled: true,
    createTime: '2024-01-01 10:00:00',
    updateTime: '2024-01-15 10:00:00'
  },
  {
    id: 2,
    ruleName: '护理服务自动派单规则',
    serviceType: 'nursing',
    radius: 8,
    matchConditions: {
      includeUpcoming: true,
      minRating: 4.5,
      maxDistance: 8
    },
    pushSettings: {
      pushCount: 5,
      timeout: 45
    },
    enabled: true,
    createTime: '2024-01-02 11:00:00',
    updateTime: '2024-01-16 11:00:00'
  },
  {
    id: 3,
    ruleName: '到家服务自动派单规则',
    serviceType: 'home',
    radius: 10,
    matchConditions: {
      includeUpcoming: false,
      minRating: 3.5,
      maxDistance: 10
    },
    pushSettings: {
      pushCount: 4,
      timeout: 60
    },
    enabled: false,
    createTime: '2024-01-03 12:00:00',
    updateTime: '2024-01-17 12:00:00'
  },
  {
    id: 4,
    ruleName: '产康服务高评分派单规则',
    serviceType: 'maternity',
    radius: 3,
    matchConditions: {
      includeUpcoming: true,
      minRating: 4.8,
      maxDistance: 3
    },
    pushSettings: {
      pushCount: 2,
      timeout: 20
    },
    enabled: true,
    createTime: '2024-01-04 13:00:00',
    updateTime: '2024-01-18 13:00:00'
  },
  {
    id: 5,
    ruleName: '护理服务快速派单规则',
    serviceType: 'nursing',
    radius: 6,
    matchConditions: {
      includeUpcoming: true,
      minRating: 4.0,
      maxDistance: 6
    },
    pushSettings: {
      pushCount: 6,
      timeout: 15
    },
    enabled: true,
    createTime: '2024-01-05 14:00:00',
    updateTime: '2024-01-19 14:00:00'
  }
]

// ==================== 初始状态 ====================

const initialState = {
  // 规则列表状态
  list: {
    fetching: false,
    error: null,
    query: {},
    data: {
      list: [],
      total: 0,
      pageNumber: 1,
      pageSize: 20
    }
  },
  // 规则详情状态
  detail: {
    fetching: false,
    error: null,
    data: null
  },
  // 创建规则操作
  create: {
    fetching: false,
    error: null
  },
  // 更新规则操作
  update: {
    fetching: false,
    error: null
  },
  // 删除规则操作
  delete: {
    fetching: false,
    error: null
  },
  // 启用/禁用规则操作
  toggle: {
    fetching: false,
    error: null
  }
}

// ==================== 异步Action ====================

/**
 * 异步获取规则列表的thunk action
 * @param {Object} query - 查询参数对象
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchDispatchingRules = createAsyncThunk(
  'dispatcher/fetchDispatchingRules',
  async (query = {}, { rejectWithValue }) => {
    try {
      // TODO: 对接真实API
      // const response = await getDispatchingRules(query)
      // return {
      //   data: response.data,
      //   query
      // }
      
      // 模拟API响应
      return new Promise((resolve) => {
        setTimeout(() => {
          let filteredRules = [...mockRules]
          
          // 根据规则名称筛选
          if (query.ruleName) {
            filteredRules = filteredRules.filter(rule => 
              rule.ruleName.includes(query.ruleName)
            )
          }
          
          // 根据服务类型筛选
          if (query.serviceType) {
            filteredRules = filteredRules.filter(rule => 
              rule.serviceType === query.serviceType
            )
          }
          
          // 根据启用状态筛选
          if (query.enabled !== undefined && query.enabled !== null && query.enabled !== '') {
            const enabled = query.enabled === true || query.enabled === 'true'
            filteredRules = filteredRules.filter(rule => rule.enabled === enabled)
          }
          
          // 分页处理
          const page = query.page || 1
          const pageSize = query.pageSize || 20
          const start = (page - 1) * pageSize
          const end = start + pageSize
          const paginatedRules = filteredRules.slice(start, end)
          
          resolve({
            data: {
              list: paginatedRules,
              total: filteredRules.length,
              pageNumber: page,
              pageSize: pageSize
            },
            query
          })
        }, 300) // 模拟网络延迟
      })
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取规则列表失败')
    }
  }
)

/**
 * 异步获取规则详情的thunk action
 * @param {string|number} ruleId - 规则ID
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchDispatchingRuleDetail = createAsyncThunk(
  'dispatcher/fetchDispatchingRuleDetail',
  async (ruleId, { rejectWithValue }) => {
    try {
      // TODO: 对接真实API
      // const response = await getDispatchingRuleDetail(ruleId)
      // return {
      //   data: response.data
      // }
      
      // 模拟API响应
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const rule = mockRules.find(r => String(r.id) === String(ruleId))
          if (rule) {
            resolve({
              data: { ...rule }
            })
          } else {
            reject({ response: { data: '规则不存在' } })
          }
        }, 200)
      })
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取规则详情失败')
    }
  }
)

/**
 * 异步创建规则的thunk action
 * @param {Object} data - 规则数据
 * @returns {Promise} 返回处理后的API响应数据
 */
export const createDispatchingRuleAction = createAsyncThunk(
  'dispatcher/createDispatchingRule',
  async (data, { rejectWithValue }) => {
    try {
      // TODO: 对接真实API
      // const response = await createDispatchingRule(data)
      // return {
      //   data: response.data
      // }
      
      // 模拟API响应
      return new Promise((resolve) => {
        setTimeout(() => {
          const newRule = {
            id: mockRules.length > 0 ? Math.max(...mockRules.map(r => r.id)) + 1 : 1,
            ...data,
            createTime: new Date().toLocaleString('zh-CN', { 
              year: 'numeric', 
              month: '2-digit', 
              day: '2-digit', 
              hour: '2-digit', 
              minute: '2-digit', 
              second: '2-digit',
              hour12: false
            }).replace(/\//g, '-'),
            updateTime: new Date().toLocaleString('zh-CN', { 
              year: 'numeric', 
              month: '2-digit', 
              day: '2-digit', 
              hour: '2-digit', 
              minute: '2-digit', 
              second: '2-digit',
              hour12: false
            }).replace(/\//g, '-')
          }
          mockRules.push(newRule)
          resolve({
            data: newRule
          })
        }, 300)
      })
    } catch (error) {
      return rejectWithValue(error.response?.data || '创建规则失败')
    }
  }
)

/**
 * 异步更新规则的thunk action
 * @param {Object} params - 参数对象
 * @param {string|number} params.ruleId - 规则ID
 * @param {Object} params.data - 更新数据
 * @returns {Promise} 返回处理后的API响应数据
 */
export const updateDispatchingRuleAction = createAsyncThunk(
  'dispatcher/updateDispatchingRule',
  async ({ ruleId, data }, { rejectWithValue }) => {
    try {
      // TODO: 对接真实API
      // const response = await updateDispatchingRule(ruleId, data)
      // return {
      //   data: response.data,
      //   ruleId
      // }
      
      // 模拟API响应
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const ruleIndex = mockRules.findIndex(r => String(r.id) === String(ruleId))
          if (ruleIndex !== -1) {
            mockRules[ruleIndex] = {
              ...mockRules[ruleIndex],
              ...data,
              updateTime: new Date().toLocaleString('zh-CN', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit', 
                hour: '2-digit', 
                minute: '2-digit', 
                second: '2-digit',
                hour12: false
              }).replace(/\//g, '-')
            }
            resolve({
              data: mockRules[ruleIndex],
              ruleId
            })
          } else {
            reject({ response: { data: '规则不存在' } })
          }
        }, 300)
      })
    } catch (error) {
      return rejectWithValue(error.response?.data || '更新规则失败')
    }
  }
)

/**
 * 异步删除规则的thunk action
 * @param {string|number} ruleId - 规则ID
 * @returns {Promise} 返回处理后的API响应数据
 */
export const deleteDispatchingRuleAction = createAsyncThunk(
  'dispatcher/deleteDispatchingRule',
  async (ruleId, { rejectWithValue }) => {
    try {
      // TODO: 对接真实API
      // const response = await deleteDispatchingRule(ruleId)
      // return {
      //   data: response.data,
      //   ruleId
      // }
      
      // 模拟API响应
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const ruleIndex = mockRules.findIndex(r => String(r.id) === String(ruleId))
          if (ruleIndex !== -1) {
            mockRules.splice(ruleIndex, 1)
            resolve({
              data: { success: true, message: '删除成功' },
              ruleId
            })
          } else {
            reject({ response: { data: '规则不存在' } })
          }
        }, 300)
      })
    } catch (error) {
      return rejectWithValue(error.response?.data || '删除规则失败')
    }
  }
)

/**
 * 异步启用/禁用规则的thunk action
 * @param {Object} params - 参数对象
 * @param {string|number} params.ruleId - 规则ID
 * @param {boolean} params.enabled - 是否启用
 * @returns {Promise} 返回处理后的API响应数据
 */
export const toggleDispatchingRuleAction = createAsyncThunk(
  'dispatcher/toggleDispatchingRule',
  async ({ ruleId, enabled }, { rejectWithValue }) => {
    try {
      // TODO: 对接真实API
      // const response = await toggleDispatchingRule(ruleId, enabled)
      // return {
      //   data: response.data,
      //   ruleId,
      //   enabled
      // }
      
      // 模拟API响应
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const rule = mockRules.find(r => String(r.id) === String(ruleId))
          if (rule) {
            rule.enabled = enabled
            rule.updateTime = new Date().toLocaleString('zh-CN', { 
              year: 'numeric', 
              month: '2-digit', 
              day: '2-digit', 
              hour: '2-digit', 
              minute: '2-digit', 
              second: '2-digit',
              hour12: false
            }).replace(/\//g, '-')
            resolve({
              data: { success: true, message: enabled ? '已启用' : '已禁用' },
              ruleId,
              enabled
            })
          } else {
            reject({ response: { data: '规则不存在' } })
          }
        }, 300)
      })
    } catch (error) {
      return rejectWithValue(error.response?.data || '操作失败')
    }
  }
)

// ==================== Redux Slice ====================

const dispatcherSlice = createSlice({
  name: 'dispatcher',
  initialState,
  reducers: {
    /**
     * 重置规则列表状态
     */
    resetDispatcherListState: (state) => {
      state.list = {
        fetching: false,
        error: null,
        query: {},
        data: {
          list: [],
          total: 0,
          pageNumber: 1,
          pageSize: 20
        }
      }
    },
    /**
     * 重置规则详情状态
     */
    resetDispatcherDetailState: (state) => {
      state.detail = {
        fetching: false,
        error: null,
        data: null
      }
    },
    /**
     * 重置创建规则操作
     */
    resetCreateState: (state) => {
      state.create = {
        fetching: false,
        error: null
      }
    },
    /**
     * 重置更新规则操作
     */
    resetUpdateState: (state) => {
      state.update = {
        fetching: false,
        error: null
      }
    },
    /**
     * 重置删除规则操作
     */
    resetDeleteState: (state) => {
      state.delete = {
        fetching: false,
        error: null
      }
    },
    /**
     * 重置启用/禁用规则操作
     */
    resetToggleState: (state) => {
      state.toggle = {
        fetching: false,
        error: null
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // ========== 获取规则列表 ==========
      .addCase(fetchDispatchingRules.pending, (state) => {
        state.list.fetching = true
        state.list.error = null
      })
      .addCase(fetchDispatchingRules.fulfilled, (state, action) => {
        state.list.fetching = false
        state.list.error = null
        state.list.query = action.payload.query || {}
        state.list.data = action.payload.data || {
          list: [],
          total: 0,
          pageNumber: 1,
          pageSize: 20
        }
      })
      .addCase(fetchDispatchingRules.rejected, (state, action) => {
        state.list.fetching = false
        state.list.error = action.payload
        state.list.data = {
          list: [],
          total: 0,
          pageNumber: 1,
          pageSize: 20
        }
      })
      // ========== 获取规则详情 ==========
      .addCase(fetchDispatchingRuleDetail.pending, (state) => {
        state.detail.fetching = true
        state.detail.error = null
      })
      .addCase(fetchDispatchingRuleDetail.fulfilled, (state, action) => {
        state.detail.fetching = false
        state.detail.error = null
        state.detail.data = action.payload.data
      })
      .addCase(fetchDispatchingRuleDetail.rejected, (state, action) => {
        state.detail.fetching = false
        state.detail.error = action.payload
        state.detail.data = null
      })
      // ========== 创建规则 ==========
      .addCase(createDispatchingRuleAction.pending, (state) => {
        state.create.fetching = true
        state.create.error = null
      })
      .addCase(createDispatchingRuleAction.fulfilled, (state) => {
        state.create.fetching = false
        state.create.error = null
      })
      .addCase(createDispatchingRuleAction.rejected, (state, action) => {
        state.create.fetching = false
        state.create.error = action.payload
      })
      // ========== 更新规则 ==========
      .addCase(updateDispatchingRuleAction.pending, (state) => {
        state.update.fetching = true
        state.update.error = null
      })
      .addCase(updateDispatchingRuleAction.fulfilled, (state, action) => {
        state.update.fetching = false
        state.update.error = null
        // 如果列表中有该规则，更新其数据
        const ruleId = action.payload.ruleId
        const rule = state.list.data.list.find(item => String(item.id) === String(ruleId))
        if (rule && action.payload.data) {
          Object.assign(rule, action.payload.data)
        }
      })
      .addCase(updateDispatchingRuleAction.rejected, (state, action) => {
        state.update.fetching = false
        state.update.error = action.payload
      })
      // ========== 删除规则 ==========
      .addCase(deleteDispatchingRuleAction.pending, (state) => {
        state.delete.fetching = true
        state.delete.error = null
      })
      .addCase(deleteDispatchingRuleAction.fulfilled, (state, action) => {
        state.delete.fetching = false
        state.delete.error = null
        // 从列表中移除该规则
        const ruleId = action.payload.ruleId
        state.list.data.list = state.list.data.list.filter(item => String(item.id) !== String(ruleId))
        state.list.data.total = Math.max(0, state.list.data.total - 1)
      })
      .addCase(deleteDispatchingRuleAction.rejected, (state, action) => {
        state.delete.fetching = false
        state.delete.error = action.payload
      })
      // ========== 启用/禁用规则 ==========
      .addCase(toggleDispatchingRuleAction.pending, (state) => {
        state.toggle.fetching = true
        state.toggle.error = null
      })
      .addCase(toggleDispatchingRuleAction.fulfilled, (state, action) => {
        state.toggle.fetching = false
        state.toggle.error = null
        // 如果列表中有该规则，更新其启用状态
        const ruleId = action.payload.ruleId
        const rule = state.list.data.list.find(item => String(item.id) === String(ruleId))
        if (rule) {
          rule.enabled = action.payload.enabled
        }
      })
      .addCase(toggleDispatchingRuleAction.rejected, (state, action) => {
        state.toggle.fetching = false
        state.toggle.error = action.payload
      })
  }
})

export const {
  resetDispatcherListState,
  resetDispatcherDetailState,
  resetCreateState,
  resetUpdateState,
  resetDeleteState,
  resetToggleState
} = dispatcherSlice.actions

export default dispatcherSlice.reducer

